import { Link } from 'react-router-dom';
import { Phone, User, Rss } from 'lucide-react';
import { useJournalSettings } from '../../../hooks/useJournalSettings';
import { footerColumns } from '../../../config/footerConfig';
import { API_BASE_URL } from '../../../config/apiConfig';

export function WarmBroadsheetFooter() {
  const currentYear = new Date().getFullYear();
  const settings = useJournalSettings();

  return (
    <footer className="bg-vellum-2 border-t-[3px] border-espresso mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div className="lg:col-span-1">
            {settings.settingsLoading ? (
              <div aria-hidden="true">
                <div className="h-6 w-24 bg-dust/50 rounded animate-pulse mb-3" />
                <div className="space-y-1.5 mb-3">
                  <div className="h-3.5 w-full bg-dust/40 rounded animate-pulse" />
                  <div className="h-3.5 w-5/6 bg-dust/40 rounded animate-pulse" />
                </div>
                <div className="space-y-1.5">
                  <div className="h-2.5 w-2/3 bg-dust/40 rounded animate-pulse" />
                  <div className="h-2.5 w-3/4 bg-dust/40 rounded animate-pulse" />
                  <div className="h-2.5 w-1/2 bg-dust/40 rounded animate-pulse" />
                </div>
              </div>
            ) : (
              <>
                <span className="font-display text-xl font-bold text-espresso">{settings.journalAbbreviation}</span>
                <p className="font-prose text-sm text-ink-2 leading-relaxed mt-2 mb-3">
                  {settings.journalName}. A peer-reviewed, open-access journal committed to advancing evidence-based medicine and making research freely available to all.
                </p>
                <div className="font-prose text-[10px] text-ink-2 leading-loose">
                  {settings.publisherName} Ltd<br />
                  {settings.address}<br />
                  {settings.contactEmail} · {settings.contactPhonePrimary}
                </div>
              </>
            )}
          </div>

          {footerColumns.map((column, colIdx) => (
            <div key={column.title}>
              <h3 className="font-prose text-[10px] font-bold uppercase tracking-[0.08em] text-espresso mb-3.5 pb-2 border-b border-dust">{column.title}</h3>
              <ul className={colIdx === footerColumns.length - 1 ? 'space-y-2 mb-6' : 'space-y-2'}>
                {column.links.map((link) => (
                  <li key={link.name}>
                    <Link to={link.path} className="font-prose text-sm text-ink-2 hover:text-brick flex items-center transition-colors">
                      <link.icon className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                      <span>{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              {colIdx === footerColumns.length - 1 && (
                settings.settingsLoading ? (
                  <div className="text-sm" aria-hidden="true">
                    <div className="h-3 w-28 bg-dust/40 rounded animate-pulse mb-1.5" />
                    <div className="h-2.5 w-20 bg-dust/40 rounded animate-pulse mb-2" />
                    <div className="h-3 w-24 bg-dust/40 rounded animate-pulse" />
                  </div>
                ) : (
                  <div className="text-sm">
                    <div className="flex items-start gap-2 mb-1.5">
                      <User className="h-3.5 w-3.5 text-ink-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-prose text-ink font-medium leading-tight">{settings.contactPersonName}</p>
                        <p className="font-prose text-[10px] text-ink-2 leading-tight">{settings.contactPersonRole}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-ink-2 mt-2">
                      <Phone className="h-3.5 w-3.5 text-ink-2 flex-shrink-0" />
                      <a href={`tel:${settings.contactPhoneSecondary.replace(/\s+/g, '')}`} className="font-prose text-sm hover:text-brick transition-colors">{settings.contactPhoneSecondary}</a>
                    </div>
                  </div>
                )
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-dust mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          {settings.settingsLoading ? (
            <>
              <span className="inline-block h-2.5 w-40 bg-dust/40 rounded animate-pulse" aria-hidden="true" />
              <span className="inline-block h-2.5 w-48 bg-dust/40 rounded animate-pulse" aria-hidden="true" />
            </>
          ) : (
            <>
              <span className="font-prose text-[10px] text-ink-2">
                &copy; {currentYear} {settings.journalName} · CC BY-NC 4.0
              </span>
              <span className="font-prose text-[10px] text-ink-2">
                ISSN {settings.issnOnline} (Online) · ISSN {settings.issnPrint} (Print)
              </span>
              <a
                href={`${API_BASE_URL}/api/journal/rss.xml`}
                className="font-prose text-[10px] text-ink-2 hover:text-brick transition-colors flex items-center gap-1"
              >
                <Rss className="h-3 w-3" />
                RSS Feed
              </a>
            </>
          )}
        </div>
        <p className="font-prose text-xs text-ink-2 leading-relaxed mt-4 max-w-4xl">
          This is a diamond open access journal, published since 2025 by{' '}
          <a href="https://opensciencepress.com/" target="_blank" rel="noopener noreferrer" className="text-brick underline hover:no-underline">
            Open Science Press
          </a>{' '}
          under the terms of the{' '}
          <a
            href="https://creativecommons.org/licenses/by-nc/4.0/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brick underline hover:no-underline"
          >
            Creative Commons Attribution-NonCommercial 4.0 International License
          </a>
          , which permits any non-commercial use, distribution, and reproduction in any medium, provided
          the original work is properly cited. For commercial use; authors retain copyright. Submission
          grants the journal the right of first publication.
        </p>
      </div>
    </footer>
  );
}

export default WarmBroadsheetFooter;
